import { useCallback, useEffect, useRef, useState } from 'react';

import { alterarJogador, listarJogadores, listarTurmas } from '../api/jogadores';
import type { Jogador, JogadoresDaTurma, TurmaResumo } from '../api/jogadores';
import { EdicaoDoJogador } from './EdicaoDoJogador';
import type { Alteracao } from './EdicaoDoJogador';
import { manterSeDisponivel, useTurmaEmUso } from '../../../shared/turmaEmUso';

type Carga<T> = { tipo: 'carregando' } | { tipo: 'erro' } | { tipo: 'pronto'; dados: T };

const SITUACAO = { ativo: 'Ativo', inativo: 'Inativo', bloqueado: 'Bloqueado' } as const;

const formatarData = (valor: string | null) =>
    valor ? new Date(valor).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

/** Duas setas correndo uma atrás da outra: o desenho é o próprio verbo. */
function SetasDeSincronismo() {
    return (
        <svg viewBox="0 0 24 24" width="16" height="16" role="img" aria-hidden="true" focusable="false">
            <path
                d="M20 11a8 8 0 0 0-13.7-5.6L4 7.6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />
            <path d="M4 3v5h5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path
                d="M4 13a8 8 0 0 0 13.7 5.6L20 16.4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />
            <path d="M20 21v-5h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

interface JogadoresPainelProps {
    /** Vindos da carta do Andamento: a tela abre já na turma e no grupo clicados. */
    turmaInicial?: string;
    grupoInicial?: string;
    csrfToken?: string;
}

export function JogadoresPainel({ turmaInicial, grupoInicial, csrfToken }: JogadoresPainelProps = {}) {
    const [turmas, setTurmas] = useState<Carga<TurmaResumo[]>>({ tipo: 'carregando' });
    const [turmaId, setTurmaId] = useTurmaEmUso(turmaInicial);
    const [grupoFiltro, setGrupoFiltro] = useState<string | null>(grupoInicial ?? null);
    const [jogadores, setJogadores] = useState<Carga<JogadoresDaTurma>>({ tipo: 'carregando' });
    const [emEdicao, setEmEdicao] = useState<string | null>(null);
    //: Enquanto relê, as setas giram. O estado mora no botão porque é ele que a
    //: folha de estilo anima — e porque a releitura automática não pisca a tela.
    const [sincronizando, setSincronizando] = useState(false);
    // Fechar o modal devolve o foco ao lápis que o abriu.
    const lapis = useRef(new Map<string, HTMLButtonElement | null>());

    const carregarTurmas = useCallback(async () => {
        setTurmas({ tipo: 'carregando' });
        try {
            const dados = await listarTurmas();
            setTurmas({ tipo: 'pronto', dados });
            setTurmaId((atual) => manterSeDisponivel(atual, dados.map((turma) => turma.id), dados[0]?.id ?? null));
        } catch {
            setTurmas({ tipo: 'erro' });
        }
    }, []);

    const carregarJogadores = useCallback(async (alvo: string, silencioso = false) => {
        if (!silencioso) setJogadores({ tipo: 'carregando' });
        setSincronizando(true);
        try {
            setJogadores({ tipo: 'pronto', dados: await listarJogadores(alvo) });
        } catch {
            setJogadores({ tipo: 'erro' });
        } finally {
            setSincronizando(false);
        }
    }, []);

    useEffect(() => {
        void carregarTurmas();
    }, [carregarTurmas]);

    // Clicar noutro grupo no Andamento troca o filtro sem remontar a tela.
    useEffect(() => {
        if (turmaInicial) setTurmaId(turmaInicial);
        setGrupoFiltro(grupoInicial ?? null);
    }, [turmaInicial, grupoInicial]);

    useEffect(() => {
        if (turmaId) void carregarJogadores(turmaId);
    }, [turmaId, carregarJogadores]);

    useEffect(() => {
        if (!turmaId) return;
        // 15s: a lista muda quando alguém entra, sai ou troca de tablet, e
        // ninguém deveria precisar lembrar de apertar um botão para ver isso.
        const ciclo = setInterval(() => void carregarJogadores(turmaId, true), 15_000);
        return () => clearInterval(ciclo);
    }, [turmaId, carregarJogadores]);

    if (turmas.tipo === 'carregando') return <p className="estado-variaveis">Carregando turmas…</p>;
    if (turmas.tipo === 'erro') {
        return (
            <p className="estado-variaveis mensagem-login">
                Não foi possível carregar as turmas.{' '}
                <button className="botao-texto" type="button" onClick={() => void carregarTurmas()}>
                    Tentar novamente
                </button>
            </p>
        );
    }
    if (!turmas.dados.length) return <p className="estado-variaveis">Nenhuma turma cadastrada.</p>;

    const todos = jogadores.tipo === 'pronto' ? jogadores.dados.jogadores : [];
    const visiveis = todos.filter((j) => !grupoFiltro || j.grupo_id === grupoFiltro);
    const comTablet = visiveis.filter((j) => j.tablet).length;
    const editando = todos.find((j) => j.id === emEdicao) ?? null;

    const fecharEdicao = () => {
        const alvo = emEdicao;
        setEmEdicao(null);
        if (alvo) lapis.current.get(alvo)?.focus();
    };

    const salvar = async (alteracao: Alteracao) => {
        if (!turmaId || !editando) return;
        await alterarJogador({
            id: editando.id,
            turmaId,
            campo: alteracao.campo,
            valor: alteracao.valor,
            valorAnterior: alteracao.valorAnterior,
            novoLider: alteracao.novoLider,
            csrfToken: csrfToken ?? '',
        });
        // Relê a turma: uma troca de liderança muda a linha de outra pessoa também.
        await carregarJogadores(turmaId, true);
    };

    return (
        <div className="jogadores">
            <div className="barra-jogadores">
                <label className="campo-turma">
                    Turma
                    <select value={turmaId ?? ''} onChange={(e) => setTurmaId(e.target.value)}>
                        {turmas.dados.map((turma) => (
                            <option key={turma.id} value={turma.id}>
                                {turma.id}{turma.nome ? ` · ${turma.nome}` : ''} · {turma.participantes} participantes{turma.ativa ? '' : ' (inativa)'}
                            </option>
                        ))}
                    </select>
                </label>
                {grupoFiltro && (
                    <p className="filtro-grupo">
                        Grupo {grupoFiltro}
                        <button
                            type="button"
                            className="botao-texto"
                            aria-label="Limpar filtro de grupo"
                            onClick={() => setGrupoFiltro(null)}
                        >
                            limpar
                        </button>
                    </p>
                )}
                {jogadores.tipo === 'pronto' && (
                    <p className="resumo-jogadores">
                        {comTablet} de {visiveis.length} jogadores com tablet vinculado
                    </p>
                )}
                <button
                    className="botao-sincronizar"
                    type="button"
                    aria-label="Atualizar agora"
                    title="Atualizar agora"
                    data-sincronizando={sincronizando ? '' : undefined}
                    disabled={!turmaId}
                    onClick={() => turmaId && void carregarJogadores(turmaId, true)}
                >
                    <SetasDeSincronismo />
                </button>
            </div>

            {jogadores.tipo === 'carregando' && <p className="estado-variaveis">Carregando jogadores…</p>}
            {jogadores.tipo === 'erro' && (
                <p className="estado-variaveis mensagem-login">Não foi possível carregar os jogadores desta turma.</p>
            )}
            {jogadores.tipo === 'pronto' && !visiveis.length && (
                <p className="estado-variaveis">
                    {grupoFiltro
                        ? `Nenhum participante no grupo ${grupoFiltro} desta turma.`
                        : 'Esta turma ainda não tem participantes.'}
                </p>
            )}
            {jogadores.tipo === 'pronto' && visiveis.length > 0 && (
                <table className="tabela-jogadores">
                    <thead>
                        <tr>
                            <th scope="col">Grupo</th>
                            <th scope="col">Matrícula</th>
                            <th scope="col">Nome</th>
                            <th scope="col">Papel no grupo</th>
                            <th scope="col">Situação</th>
                            <th scope="col">Tablet vinculado</th>
                            <th scope="col">Último acesso</th>
                            <th scope="col" className="coluna-acao">
                                <span className="so-leitor-de-tela">Editar</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {visiveis.map((jogador) => (
                            <tr key={jogador.id}>
                                <td>{jogador.grupo_id ?? '—'}</td>
                                <td>{jogador.login}</td>
                                <td>{jogador.nome ?? '—'}</td>
                                <td>{jogador.lider ? <span className="selo-lider">Líder</span> : 'Participante'}</td>
                                <td>
                                    <span className={`selo-situacao selo-${jogador.situacao}`}>{SITUACAO[jogador.situacao]}</span>
                                </td>
                                <td>{jogador.tablet ? <code>{jogador.tablet}</code> : <span className="sem-tablet">Sem tablet</span>}</td>
                                <td className="numero">{formatarData(jogador.ultimo_acesso)}</td>
                                <td className="coluna-acao">
                                    <button
                                        type="button"
                                        className="botao-lapis"
                                        aria-label={`Editar ${jogador.login}`}
                                        ref={(elemento) => {
                                            lapis.current.set(jogador.id, elemento);
                                        }}
                                        onClick={() => setEmEdicao(jogador.id)}
                                    >
                                        ✎
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {editando && jogadores.tipo === 'pronto' && turmaId && (
                <EdicaoDoJogador
                    jogador={editando}
                    turmaId={turmaId}
                    grupos={jogadores.dados.grupos}
                    companheiros={todos.filter(
                        (outro) => outro.grupo_id === editando.grupo_id && outro.id !== editando.id,
                    )}
                    aoFechar={fecharEdicao}
                    aoSalvar={salvar}
                />
            )}
        </div>
    );
}
